// Data transformation utility to work directly with points-charts.json
import { parseISO, format } from 'date-fns';

/**
 * Extract resort code from resort name
 * @param {string} resortName - Full resort name from JSON
 * @returns {string} Resort code
 */
export const getResortCodeFromName = (resortName) => {
  const cleanName = resortName.replace('DVC - ', '');
  
  const codeMap = {
    'Aulani': 'AUL',
    'Animal Kingdom Villas - Jambo House': 'AKV',
    'Animal Kingdom Villas - Kidani Village': 'AKL',
    'Bay Lake Tower': 'BLT',
    'Beach Club Villas': 'BCV',
    'BoardWalk Villas': 'BWV',
    'Copper Creek': 'CCV',
    'Hilton Head': 'HIL',
    'Vero Beach': 'VB',
    'Old Key West': 'OKW',
    'Polynesian': 'PVB',
    'Riviera': 'RIV',
    'Saratoga Springs': 'SSR',
    'Grand Californian': 'VGC',
    'Grand Floridian': 'VGF',
    'Disneyland Hotel': 'VDH'
  };
  
  for (const [key, code] of Object.entries(codeMap)) {
    if (cleanName.includes(key)) {
      return code;
    }
  }
  
  return cleanName.split(' ').slice(0, 3).map(word => word[0]).join('').toUpperCase();
};

/**
 * Get points for a specific stay using points-charts.json data directly
 * @param {Array} newSchemaData - Array of resort data from points-charts.json
 * @param {Object} stay - Stay details
 * @returns {number} Points required for the stay
 */
export const calculatePointsFromNewSchema = (newSchemaData, stay) => {
  const { resort, checkIn, lengthOfStay, roomType, viewType, year = 2025 } = stay;
  
  if (!resort || !checkIn || !lengthOfStay || !roomType) {
    return 0;
  }

  // Find resort data by matching resort code
  const resortData = newSchemaData.find(r => {
    const resortCode = getResortCodeFromName(r.ResortName);
    return resortCode === resort && r.Year === year;
  });
  
  if (!resortData) {
    return 0;
  }

  const checkInDate = typeof checkIn === 'string' ? parseISO(checkIn) : checkIn;
  
  // Find exact room configuration matching both room type and view
  const roomConfig = resortData.Rooms.find(room => {
    const roomMatches = room.type === roomType;
    const viewMatches = !viewType || room.view === viewType;
    return roomMatches && viewMatches;
  });

  if (!roomConfig) {
    return 0;
  }

  // Calculate points day-by-day based on day of the week
  let totalPoints = 0;
  
  for (let dayOffset = 0; dayOffset < lengthOfStay; dayOffset++) {
    // Calculate the date for this specific day of the stay
    const currentDate = new Date(checkInDate);
    currentDate.setDate(currentDate.getDate() + dayOffset);
    
    // Find the travel period for this specific date (periods can change during a stay)
    const periodForThisDay = roomConfig.TravelPeriods.find(period => {
      const startDate = parseISO(period.Start);
      const endDate = parseISO(period.End);
      return currentDate >= startDate && currentDate <= endDate;
    });
    
    if (!periodForThisDay) {
      console.warn(`No travel period found for date ${currentDate.toISOString().split('T')[0]} at ${resort}`);
      continue; // Skip this day if no period found
    }
    
    // Get day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const dayOfWeek = currentDate.getDay();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    // Friday (5) and Saturday (6) use FriToSat rate
    // Sunday (0), Monday (1), Tuesday (2), Wednesday (3), Thursday (4) use SunToThur rate
    const isFridayOrSaturday = dayOfWeek === 5 || dayOfWeek === 6;
    const pointsForThisDay = isFridayOrSaturday ? periodForThisDay.FriToSat : periodForThisDay.SunToThur;
    
    // Debug logging (can be removed later)
    if (process.env.NODE_ENV === 'development') {
      console.log(`Day ${dayOffset + 1}: ${currentDate.toISOString().split('T')[0]} (${dayNames[dayOfWeek]}) = ${pointsForThisDay} points (${isFridayOrSaturday ? 'FriToSat' : 'SunToThur'} rate)`);
    }
    
    totalPoints += pointsForThisDay;
  }
  
  // Debug summary (can be removed later)
  if (process.env.NODE_ENV === 'development') {
    console.log(`Total points for ${lengthOfStay} nights at ${resort} (${roomType}, ${viewType}): ${totalPoints}`);
  }
  
  return totalPoints;
};

/**
 * Get available room types for a resort directly from points-charts.json
 * @param {Array} newSchemaData - Array of resort data from points-charts.json
 * @param {string} resortCode - Resort code
 * @param {number} year - Year
 * @returns {Array} Available room types (exact strings from JSON)
 */
export const getAvailableRoomTypesFromNewSchema = (newSchemaData, resortCode, year = 2025) => {
  if (!newSchemaData || !Array.isArray(newSchemaData) || !resortCode) {
    return [];
  }

  const resortData = newSchemaData.find(r => {
    const code = getResortCodeFromName(r.ResortName);
    return code === resortCode && r.Year === year;
  });
  
  if (!resortData || !resortData.Rooms || !Array.isArray(resortData.Rooms)) {
    return [];
  }

  // Return exact room types from JSON without simplification
  const roomTypes = [...new Set(resortData.Rooms.map(room => room.type).filter(type => type))];
  return roomTypes;
};

/**
 * Get available view types for a resort and room type directly from points-charts.json
 * @param {Array} newSchemaData - Array of resort data from points-charts.json
 * @param {string} resortCode - Resort code
 * @param {string} roomType - Exact room type string from JSON
 * @param {number} year - Year
 * @returns {Array} Available view types (exact codes from JSON)
 */
export const getAvailableViewTypesFromNewSchema = (newSchemaData, resortCode, roomType, year = 2025) => {
  if (!newSchemaData || !Array.isArray(newSchemaData) || !resortCode || !roomType) {
    return [];
  }

  const resortData = newSchemaData.find(r => {
    const code = getResortCodeFromName(r.ResortName);
    return code === resortCode && r.Year === year;
  });
  
  if (!resortData || !resortData.Rooms || !Array.isArray(resortData.Rooms)) {
    return [];
  }

  // Return exact view codes from JSON for the specific room type
  const viewTypes = [...new Set(
    resortData.Rooms
      .filter(room => room.type === roomType)
      .map(room => room.view)
      .filter(view => view)
  )];
  
  return viewTypes;
};
